import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hash } from '@/utils/hash';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import useGetUser from '../hooks/useGetUser';
import useLogin from '../hooks/useLogin';
import useGoogleAuth from '../hooks/useGoogleAuth';
import GoogleImg from '@/assets/google.png';
import GifteeLogo from '@/assets/Giftee.png';
import AppleImg from '@/assets/apple.png';
import AppleWhiteImg from '@/assets/appleWhite.png';
import useReactNative from '@/utils/useReactNative';
import useAppleAuth from '../hooks/useAppleAuth';

const CLIENT_ID = `${import.meta.env.VITE_GOOGLE_CLIENT_ID}`;
const REDIRECT_URI = `${import.meta.env.VITE_APP_URL}`;

export function Login() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	const state = searchParams.get('state');
	const code = searchParams.get('code');

	const redirect = searchParams.get('redirect');

	const { user } = useGetUser();
	const { login, loginLoading } = useLogin();
	const { googleAuth, googleAuthLoading } = useGoogleAuth();
	const { appleAuth, appleAuthLoading } = useAppleAuth();

	const { isReactNative, postMessage } = useReactNative((message) => {
		if (message.message === 'GoogleAuthCode') {
			const uuid = crypto.randomUUID();
			localStorage.setItem('uuid', uuid);
			navigate('/?code=' + message.code + '&state=' + uuid);
		} else if (message.message === 'AppleAuthCode') {
			const code = message.code as string;
			const token = message.token as string;
			const firstname = (message.firstname as string) || '';
			const lastname = (message.lastname as string) || '';
			appleAuth(
				{ code, token, firstname, lastname },
				{ onSuccess: () => navigate(redirect || '/home') }
			);
		}
	});

	const isiOSWebview = /\(ip.*applewebkit(?!.*(version|crios))/i.test(
		navigator.userAgent
	);

	const [error, setError] = useState<number | null>(null);

	const registrationSchema = z.object({
		email: z.string().email({ message: t('register.error.email') }),
		password: z.string().min(8, { message: t('register.error.password') }),
	});

	const form = useForm<z.infer<typeof registrationSchema>>({
		resolver: zodResolver(registrationSchema),
		defaultValues: {
			email: '',
			password: '',
		},
	});

	const onSubmit = (values: z.infer<typeof registrationSchema>) => {
		values.password = hash(values.password);
		setError(null);
		login(values, {
			onSuccess(data) {
				if (data.status === 404 && !data.user) {
					setError(404);
					return;
				}
				if (data.status === 200 && data.user !== null) {
					setTimeout(() => {
						navigate(redirect || '/home');
					});
				}
			},
		});
	};

	useEffect(() => {
		if (!state || !code || googleAuthLoading) return;
		const localState = localStorage.getItem('uuid');
		if (state !== localState) return;
		localStorage.removeItem('uuid');
		googleAuth(
			{ code },
			{ onSuccess: () => navigate(redirect || '/home') }
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state, code, googleAuth, navigate]);

	if (user === undefined) return;

	return (
		<Card className="mx-auto -mt-9 max-w-sm self-center">
			<CardHeader>
				<img
					data-testid="giftee-logo"
					src={GifteeLogo}
					alt="Giftee"
					className="mx-auto my-6 w-[55%]"
				/>
				<CardTitle data-testid="login-title" className="text-2xl">
					{t('login.title')}
				</CardTitle>
				<CardDescription data-testid="login-description">
					{t('login.description')}
				</CardDescription>
			</CardHeader>
			<CardContent>
				{error === 404 && (
					<Alert
						variant="destructive"
						className="mb-4"
						data-testid="login-error"
					>
						<AlertCircle className="h-4 w-4" />
						<AlertTitle>{t('login.error')}</AlertTitle>
					</Alert>
				)}
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="grid gap-4"
					>
						<div className="grid gap-4">
							<div className="grid gap-2">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<Input
												data-testid="login-email-input"
												className="text-base"
												id="email"
												// type="email"
												disabled={
													loginLoading ||
													googleAuthLoading
												}
												inputMode="email"
												label={t(
													'login.input.email.label'
												)}
												placeholder={t(
													'login.input.email.placeholder'
												)}
												{...field}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="grid gap-2">
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<>
											<FormItem>
												<div className="flex items-center">
													<Label htmlFor="password">
														{t(
															'login.input.password.label'
														)}
													</Label>
													<Link
														data-testid="login-forgot-password-link"
														to="/reset-password"
														className="ml-auto inline-block text-sm underline"
													>
														{t(
															'login.forgotPassword'
														)}
													</Link>
												</div>
												<Input
													data-testid="login-password-input"
													id="password"
													type="password"
													className="text-base"
													disabled={
														loginLoading ||
														googleAuthLoading
													}
													{...field}
												/>
												<FormMessage />
											</FormItem>
										</>
									)}
								/>
							</div>
							<Button
								type="submit"
								data-testid="login-submit-button"
								className="w-full"
								isLoading={loginLoading}
								disabled={googleAuthLoading || appleAuthLoading}
							>
								{t('login.button.login')}
							</Button>
							<div
								className={
									'flex flex-col gap-2 ' +
									(!isiOSWebview ? '-mt-4' : '')
								}
							>
								<Button
									data-testid="login-google-button"
									variant="outline"
									className="mt-2 w-full"
									isLoading={googleAuthLoading}
									disabled={loginLoading || googleAuthLoading}
									onClick={async () => {
										if (!isReactNative) {
											const uuid = crypto.randomUUID();
											localStorage.setItem('uuid', uuid);
											const url =
												`https://accounts.google.com/o/oauth2/v2/auth?` +
												`client_id=${CLIENT_ID}` +
												`&redirect_uri=${REDIRECT_URI}` +
												`&response_type=code` +
												`&scope=openid%20profile%20email` +
												`&state=${uuid}`;
											window.location.href = url;
										}
										if (isReactNative) {
											postMessage({
												message: 'GoogleAuth',
											});
										}
									}}
								>
									<img
										src={GoogleImg}
										alt="Google"
										className="mr-2 inline-block h-4 w-4"
									/>
									{t('login.button.loginGoogle')}
								</Button>
								{isiOSWebview && (
									<Button
										variant="outline"
										className="w-full"
										isLoading={appleAuthLoading}
										disabled={
											loginLoading || googleAuthLoading
										}
										onClick={() => {
											if (isReactNative) {
												postMessage({
													message: 'AppleAuth',
												});
											}
										}}
									>
										<img
											src={AppleImg}
											alt="Apple"
											className="mr-2 inline-block h-4 w-4 dark:hidden"
										/>
										<img
											src={AppleWhiteImg}
											alt="Apple"
											className="mr-2 hidden h-4 w-4 dark:inline-block"
										/>
										{t('login.button.loginApple')}
									</Button>
								)}
							</div>
						</div>
						<div className="mt-4 text-center text-sm">
							{t('login.signUp.question')}
							<Link
								to="/register"
								className="ml-2 underline"
								data-testid="login-signup-link"
							>
								{t('login.signUp.link')}
							</Link>
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	);
}
